import { Link } from "react-router-dom";
import type { Car, ServiceHistory, ShftService, User } from "@/types/database";
import { CarCard } from "@/components/cars/CarCard";
import { SafeImage } from "@/components/ui/SafeImage";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import {
  getServiceHistoryForCar,
  getShftServicesForCar,
} from "@/lib/user-analytics";
import { formatCurrency, formatDate, formatNumber, imageSrc, userDisplayName } from "@/lib/utils";

function UserAvatar({ user, size = "lg" }: { user: User; size?: "lg" | "md" }) {
  const profilePhoto = imageSrc(user.profilePhoto);
  const dimensions = size === "lg" ? "h-20 w-20 text-base" : "h-14 w-14 text-sm";
  const name = userDisplayName(user);

  if (profilePhoto) {
    return (
      <SafeImage
        src={profilePhoto}
        alt={name}
        width={size === "lg" ? 80 : 56}
        height={size === "lg" ? 80 : 56}
        className={`${dimensions} rounded-full object-cover`}
      />
    );
  }

  const initials = `${user.firstName?.charAt(0) ?? ""}${user.lastName?.charAt(0) ?? ""}`.toUpperCase() || "U";

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-accent-subtle font-bold text-accent-foreground ${dimensions}`}
    >
      {initials}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-3 last:border-0">
      <span className="shrink-0 text-muted">{label}</span>
      <span className="text-right font-semibold text-foreground">{value}</span>
    </div>
  );
}

function ServiceHistoryCard({ entry }: { entry: ServiceHistory }) {
  return (
    <article className="rounded-2xl bg-canvas p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold">Maintenance record</p>
          <p className="mt-1 text-xs text-muted">{formatDate(entry.serviceDate ?? entry.created_at)}</p>
        </div>
        {entry.totalCost != null && entry.totalCost > 0 ? (
          <p className="text-sm font-bold">{formatCurrency(entry.totalCost)}</p>
        ) : null}
      </div>
      {entry.serviceType?.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {entry.serviceType.map((type) => (
            <span key={type} className="stat-pill stat-pill-surface">
              {type}
            </span>
          ))}
        </div>
      ) : null}
      {entry.serviceList?.length ? (
        <p className="mt-3 text-sm text-muted">{entry.serviceList.join(" · ")}</p>
      ) : null}
    </article>
  );
}

function ShftServiceCard({ entry }: { entry: ShftService }) {
  return (
    <article className="rounded-2xl bg-canvas p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold">{entry.serviceName ?? entry.serviceType ?? "Shift service"}</p>
          <p className="mt-1 text-xs text-muted">{formatDate(entry.scheduledDate ?? entry.created_at)}</p>
        </div>
        {entry.cost != null ? <p className="text-sm font-bold">{formatCurrency(entry.cost)}</p> : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {entry.serviceType ? <span className="stat-pill stat-pill-surface">{entry.serviceType}</span> : null}
        {entry.serviceStatus ? <span className="stat-pill stat-pill-surface">{entry.serviceStatus}</span> : null}
      </div>
    </article>
  );
}

function CarServicesSection({
  car,
  serviceHistory,
  shftServices,
}: {
  car: Car;
  serviceHistory: ServiceHistory[];
  shftServices: ShftService[];
}) {
  const history = getServiceHistoryForCar(car.carID, serviceHistory);
  const services = getShftServicesForCar(car.carID, shftServices);
  const title = [car.make, car.model, car.modelYear].filter(Boolean).join(" ");

  if (history.length === 0 && services.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-bold">{title || "Vehicle"}</h3>
        <Link to={`/cars/${car.carID}`} className="text-xs font-bold text-accent-link hover:text-foreground">
          View car
        </Link>
      </div>
      <div className="grid gap-3">
        {history.map((entry) => (
          <ServiceHistoryCard key={`history-${entry.id}`} entry={entry} />
        ))}
        {services.map((entry) => (
          <ShftServiceCard key={`shft-${entry.id}`} entry={entry} />
        ))}
      </div>
    </div>
  );
}

export function UserDetailPanel({
  user,
  cars,
  serviceHistory,
  shftServices,
}: {
  user: User;
  cars: Car[];
  serviceHistory: ServiceHistory[];
  shftServices: ShftService[];
}) {
  const name = userDisplayName(user);
  const totalServices = serviceHistory.length + shftServices.length;
  const carsWithServices = cars.filter(
    (car) =>
      getServiceHistoryForCar(car.carID, serviceHistory).length > 0 ||
      getShftServicesForCar(car.carID, shftServices).length > 0
  );

  return (
    <Workspace>
      <WorkspaceSection className="grid gap-0 lg:grid-cols-[1fr_1.1fr]">
        <div className="border-b border-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
          <UserAvatar user={user} size="lg" />
          <h1 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{name}</h1>
          {user.email ? <p className="mt-1 text-sm text-muted">{user.email}</p> : null}

          <div className="mt-3 flex flex-wrap gap-1.5">
            {user.is_admin ? <span className="stat-pill">Admin</span> : null}
            {user.country ? <span className="stat-pill">{user.country}</span> : null}
            <span className="stat-pill">{formatNumber(cars.length)} cars</span>
            <span className="stat-pill">{formatNumber(totalServices)} services</span>
          </div>

          <div className="mt-6 rounded-2xl bg-canvas px-4">
            <DetailRow label="Phone" value={user.phoneNumber} />
            <DetailRow label="Address" value={user.address} />
            <DetailRow label="Date of birth" value={formatDate(user.DOB)} />
            <DetailRow label="Member since" value={formatDate(user.created_at)} />
            <DetailRow label="Tokens" value={user.tokenAmount != null ? formatNumber(user.tokenAmount) : null} />
            <DetailRow
              label="VIN lookups"
              value={user.vinLookupAmount != null ? formatNumber(user.vinLookupAmount) : null}
            />
            <DetailRow label="User ID" value={user.userID} />
          </div>
        </div>

        <div className="p-4 sm:p-5">
          <h2 className="mb-4 font-bold">Owned vehicles</h2>
          {cars.length === 0 ? (
            <p className="text-sm text-muted">This user has no cars linked on the platform.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {cars.map((car) => (
                <CarCard key={car.carID} car={car} />
              ))}
            </div>
          )}
        </div>
      </WorkspaceSection>

      <WorkspaceSection border className="p-4 sm:p-5">
        <div className="mb-5">
          <h2 className="font-bold">Service history</h2>
          <p className="mt-1 text-sm text-muted">
            Maintenance records and Shift services grouped by vehicle.
          </p>
        </div>

        {carsWithServices.length === 0 ? (
          <div className="rounded-2xl bg-canvas px-6 py-12 text-center">
            <p className="font-bold text-foreground">No services recorded</p>
            <p className="mt-1 text-sm text-muted">This user has not logged any services yet.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {carsWithServices.map((car) => (
              <CarServicesSection
                key={car.carID}
                car={car}
                serviceHistory={serviceHistory}
                shftServices={shftServices}
              />
            ))}
          </div>
        )}
      </WorkspaceSection>
    </Workspace>
  );
}
